import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IAppRequest } from '@app/app.interface';
import { CustomMaterialModule } from '@app/core/material.module';
import { CropperDialogComponent } from '@app/dialogs-helper/cropper-dialog/cropper-dialog.component';
import {
    allowedMimeTypes,
    DEFAULT_CROPPER_DIALOG_DATA,
    guessOutputFormat,
    ICropperDialogData,
} from '@app/dialogs-helper/cropper-dialog/cropper-dialog.interface';
import { CropperDialogModule } from '@app/dialogs-helper/cropper-dialog/cropper-dialog.module';
import { ButtonModule } from '@components/button/modules/button.module';
import { LANG_DICTIONARY_TOKEN } from '@constants/diConstant';
import { FuiDestroyService } from '@services/destroy.service';
import { FileUploadModule } from 'ng2-file-upload';
import { BehaviorSubject, filter, from, map, of, switchMap, take, takeUntil } from 'rxjs';
import { pickFile, toDataURL } from 'src/shared/helpers/file-helpers';
import { IImagePickerActions, IImagePickerData } from './image-picker.interface';

@Component({
    selector: 'fui-image-picker',
    templateUrl: './image-picker.component.html',
    styleUrls: ['./image-picker.component.scss'],
    imports: [CommonModule, CustomMaterialModule, ButtonModule, FileUploadModule, CropperDialogModule],
    providers: [FuiDestroyService],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImagePickerComponent {
    readonly #destroy$ = inject(FuiDestroyService);
    private readonly dialog = inject(MatDialog);
    readonly dictionary = inject(LANG_DICTIONARY_TOKEN);

    loading$ = new BehaviorSubject<boolean>(false);

    /**
     * URL картинки
     */
    url$ = new BehaviorSubject<string | null>(null);

    /**
     * Название файла картинки
     */
    name$ = new BehaviorSubject<string | null>(null);

    /**
     * Лэйбл поля инпута
     */
    @Input() label: string;

    /**
     * Разрешённые типы файлов
     */
    @Input() mimeTypes = allowedMimeTypes;

    /**
     * Разрешить замену картинки или доступ только на просмотр
     */
    @Input() editable = true;

    /**
     * Использовать диалог редактирования картинки для кропа и ресайза
     */
    @Input() useCrop = true;

    /**
     * Настройки диалога редактирования картинки
     */
    @Input() cropperOptions: ICropperDialogData = DEFAULT_CROPPER_DIALOG_DATA;

    /**
     * Исходный URL картинки
     */
    @Input() set imageUrl(value: string) {
        this.url$.next(value ?? null);
    }

    /**
     * Исходное название файла картинки
     */
    @Input() set imageName(value: string) {
        this.name$.next(value ?? null);
    }

    /**
     * Максимальный вес картинки
     */
    @Input() maxSize = 10;

    /**
     * Ширина отображаемой превьюшки картинки, px
     */
    @Input() previewWidth: number | null = null;

    /**
     * Высота отображаемой превьюшки картинки, px
     */
    @Input() previewHeight: number | null = null;

    /**
     * Действия для операций с картинкой
     */
    @Input() actions: IImagePickerActions | null = null;

    /**
     * Событие выбора новой картинки
     */
    @Output() picked = new EventEmitter<IImagePickerData>();

    /**
     * Событие после успешной загрузки файла (возвращает результат, который вернул сервер)
     */
    @Output() uploaded = new EventEmitter<any>();

    /**
     * Событие ошибки загрузки файла
     */
    @Output() failed = new EventEmitter<unknown>();

    /**
     * Событие после удаления картинки (возвращает результат, который вернул сервер)
     */
    @Output() removed = new EventEmitter<any>();

    /**
     * Обрабатываем выбор картинки пользователем
     * Полный процесс всех действий над картинкой
     */
    processUpload() {
        from(pickFile())
            .pipe(
                take(1),

                // Проверка размера и типа файла
                switchMap((file: File) => {
                    if (!this.mimeTypes.includes(file.type)) {
                        throw new Error(this.dictionary.files_file_format_error + `: "${file.name}"`);
                    }
                    if (file.size > 1000000 * this.maxSize) {
                        throw new Error(this.dictionary.files_file_size_error);
                    }
                    return from(toDataURL(file)).pipe(
                        map((dataUrl) => ({ dataUrl, name: file.name, canCrop: allowedMimeTypes.includes(file.type) })),
                    );
                }),

                // Обрезка картинки, если затребована и тип файла подходящий
                switchMap((data) => {
                    if (!this.useCrop || !data.canCrop) {
                        return of({ save: true, box: data.dataUrl, round: null, name: data.name });
                    }
                    const dialogData: ICropperDialogData = {
                        ...this.cropperOptions,
                        outputFormat: guessOutputFormat(data.name),
                        data: data.dataUrl,
                    };
                    const dialogRef = this.dialog.open(CropperDialogComponent, {
                        disableClose: true,
                        autoFocus: true,
                        width: '600px',
                        data: dialogData,
                    });
                    dialogRef
                        .keydownEvents()
                        .pipe(takeUntil(this.#destroy$))
                        .subscribe((result) => {
                            if (result.code === 'Escape') {
                                dialogRef.close({ save: false });
                            }
                        });
                    return dialogRef.afterClosed().pipe(map((result) => ({ ...result, name: data.name })));
                }),
                filter((result) => result.save !== false),

                // Загрузка на сервер, если задана в действиях
                switchMap((result) => {
                    if (this.actions === null || !this.actions.upload) {
                        return of({ result, response: null });
                    }
                    this.loading$.next(true);
                    return this.actions.upload(result).pipe(
                        map((response: IAppRequest) => {
                            if (!response || response.is_error) {
                                throw new Error(response?.message);
                            }
                            this.loading$.next(false);
                            return { result, response };
                        }),
                    );
                }),
                takeUntil(this.#destroy$),
            )
            .subscribe({
                next: ({ result, response }) => {
                    this.loading$.next(false);
                    this.url$.next(result.box);
                    this.name$.next(result.name);
                    this.picked.emit(result);
                    if (response && response.data) {
                        this.uploaded.emit(response.data);
                    }
                },
                error: (err: unknown) => {
                    this.loading$.next(false);
                    this.failed.emit(err);
                },
            });
    }

    /**
     * Обрабатываем удаление картинки пользователем
     */
    onRemove() {
        of(null)
            .pipe(
                // Удаление на сервере, если задано в действиях
                switchMap(() => {
                    if (this.actions === null || !this.actions.remove) {
                        return of(null);
                    }
                    this.loading$.next(true);
                    return this.actions.remove().pipe(
                        map((response: IAppRequest) => {
                            if (!response || response.is_error) {
                                throw new Error(response?.message);
                            }
                            this.loading$.next(false);
                            return response;
                        }),
                    );
                }),
                takeUntil(this.#destroy$),
            )
            .subscribe({
                next: (response: IAppRequest) => {
                    this.loading$.next(false);
                    this.removed.emit(response?.data ?? null);
                    this.url$.next(null);
                    this.name$.next(null);
                },
                error: (err: unknown) => {
                    this.loading$.next(false);
                    this.failed.emit(err);
                },
            });
    }
}
